import React from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line

import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler.js";

import logo from "../../images/kc-logo.png";
import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";

const Header = styled.header`
  ${tw`flex justify-between items-center max-w-screen-xl mx-auto px-6 py-4`}
`;

const StickyWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(253, 250, 246, 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(42, 157, 143, 0.12);
  box-shadow: 0 2px 20px rgba(12, 42, 41, 0.06);
`;

export const NavLinks = tw.div`inline-block`;

export const NavLink = styled.a`
  ${tw`text-lg my-2 lg:text-sm lg:mx-6 lg:my-0 font-semibold tracking-wide transition duration-300 pb-1 border-b-2 border-transparent`}
  color: #264F5D;
  font-family: 'Nunito', sans-serif;
  &:hover {
    color: #2A9D8F;
    border-bottom-color: #2A9D8F;
  }
`;

export const PrimaryLink = styled(NavLink)`
  ${tw`lg:mx-0 px-6 py-3 rounded-lg text-white border-b-0`}
  background-color: #2A9D8F;
  font-family: 'Nunito', sans-serif;
  font-weight: 700;
  font-size: 0.875rem;
  &:hover {
    background-color: #1B6158;
    color: #fff;
    border-bottom-color: transparent;
    box-shadow: 0 4px 15px rgba(42, 157, 143, 0.3);
    transform: translateY(-1px);
  }
`;

export const LogoLink = styled(NavLink)`
  ${tw`flex items-center font-black border-b-0 text-2xl! ml-0!`}
  color: #0C2A29 !important;
  font-family: 'Cormorant Garant', serif !important;
  font-weight: 700;
  letter-spacing: 0.02em;
  &:hover {
    color: #2A9D8F !important;
    border-bottom-color: transparent;
  }

  img {
    ${tw`w-10 mr-3`}
  }
`;

export const MobileNavLinksContainer = tw.nav`flex flex-1 items-center justify-between`;
export const NavToggle = styled.button`
  ${tw`lg:hidden z-20 focus:outline-none transition duration-300 p-2`}
  color: #264F5D;
  &:hover {
    color: #2A9D8F;
  }
`;
export const MobileNavLinks = motion(styled.div`
  ${tw`lg:hidden z-10 fixed top-0 inset-x-0 mx-4 my-6 p-8 border text-center rounded-2xl text-gray-900 bg-white`}
  box-shadow: 0 20px 60px rgba(12, 42, 41, 0.15);
  border-color: rgba(42, 157, 143, 0.2);
  ${NavLinks} {
    ${tw`flex flex-col items-center`}
  }
`);

export const DesktopNavLinks = tw.nav`
  hidden lg:flex flex-1 justify-between items-center
`;

export default ({ roundedHeaderButton = false, logoLink, links, className, collapseBreakpointClass = "lg" }) => {
  const defaultLinks = [
    <NavLinks key={1}>
      <NavLink href="#services">Our Services</NavLink>
      <PrimaryLink css={roundedHeaderButton && tw`rounded-full`} href="#footer">Contact Us</PrimaryLink>
    </NavLinks>
  ];

  const { showNavLinks, animation, toggleNavbar } = useAnimatedNavToggler();
  const collapseBreakpointCss = collapseBreakPointCssMap[collapseBreakpointClass];

  const defaultLogoLink = (
    <LogoLink href="/">
      <img src={logo} alt="Kaushal Clinic Logo" />
      Kaushal Clinic
    </LogoLink>
  );

  logoLink = logoLink || defaultLogoLink;
  links = links || defaultLinks;

  return (
    <StickyWrapper>
      <Header className={className || "header-light"}>
        <DesktopNavLinks css={collapseBreakpointCss.desktopNavLinks}>
          {logoLink}
          {links}
        </DesktopNavLinks>

        <MobileNavLinksContainer css={collapseBreakpointCss.mobileNavLinksContainer}>
          {logoLink}
          <MobileNavLinks initial={{ x: "150%", display: "none" }} animate={animation} css={collapseBreakpointCss.mobileNavLinks}>
            {links}
          </MobileNavLinks>
          <NavToggle onClick={toggleNavbar} className={showNavLinks ? "open" : "closed"} aria-label="Toggle navigation">
            {showNavLinks ? <CloseIcon tw="w-6 h-6" /> : <MenuIcon tw="w-6 h-6" />}
          </NavToggle>
        </MobileNavLinksContainer>
      </Header>
    </StickyWrapper>
  );
};

const collapseBreakPointCssMap = {
  sm: {
    mobileNavLinks: tw`sm:hidden`,
    desktopNavLinks: tw`sm:flex`,
    mobileNavLinksContainer: tw`sm:hidden`
  },
  md: {
    mobileNavLinks: tw`md:hidden`,
    desktopNavLinks: tw`md:flex`,
    mobileNavLinksContainer: tw`md:hidden`
  },
  lg: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`
  },
  xl: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`
  }
};
